/** Overlay copy for the IA restructure. Deep-merged on top of each locale. */

const taxonomyEn = {
  family: 'Family',
  families: {
    all_weather: 'All Weather',
    directional: 'Directional',
    market_neutral: 'Market Neutral',
    momentum: 'Momentum',
    high_octane: 'High octane',
    intraday: 'Intraday',
  },
  familyCopy: {
    all_weather: 'Diversified regime portfolios. Lower equity beta, slower path, ballast for a portfolio.',
    directional: 'Index and macro timing. Participate in trends, step aside when health deteriorates.',
    market_neutral: 'Long/short equity with beta near zero. A diversifier, not a market call.',
    momentum: 'Multi-horizon US equity momentum. Follows strength; lags in mean-reversion.',
    high_octane: 'Levered risk-on / risk-off. Higher ceiling, deeper drawdowns.',
    intraday: 'Intraday Nasdaq futures. Systematic entries within each session, flat by its close. Nothing carries from one session to the next; the edge lives in the volatility regime.',
  },
};

const taxonomyEs = {
  family: 'Familia',
  families: {
    all_weather: 'Todo clima',
    directional: 'Direccional',
    market_neutral: 'Neutral al mercado',
    momentum: 'Impulso',
    high_octane: 'Alto octanaje',
    intraday: 'Intradía',
  },
  familyCopy: {
    all_weather: 'Carteras diversificadas por régimen. Menos beta a renta variable, camino más lento, lastre de una cartera.',
    directional: 'Timing de índices y macro. Participa en la tendencia y se aparta cuando la salud del mercado se deteriora.',
    market_neutral: 'Largo/corto con beta cerca de cero. Un diversificador, no una apuesta direccional.',
    momentum: 'Impulso de acciones estadounidenses a varios horizontes. Sigue la fuerza; se queda atrás en la reversión a la media.',
    high_octane: 'Riesgo activo / riesgo apagado apalancado. Más techo, drawdowns más profundos.',
    intraday: 'Futuros del Nasdaq intradía. Entradas sistemáticas dentro de cada sesión, plano a su cierre. Nada pasa de una sesión a la siguiente; el edge vive en el régimen de volatilidad.',
  },
};

export const iaEn = {
  nav: {
    portfolio: 'Portfolio',
    research: 'Research',
    reports: 'Reports',
    quantLab: 'Quant Lab',
    macroTerminal: 'Terminal',
    institutional: 'Institutional',
    more: 'More',
    account: 'Account',
    login: 'Log in',
    about: 'About',
  },
  taxonomy: taxonomyEn,
  disclaimer: {
    label: 'Disclaimer',
    body: 'Backtested and research equity curves are educational. They are not investment advice or a recommendation. Past performance does not guarantee future results. Live trading includes costs the charts ignore.',
  },
  strategyDetail: {
    compare: 'Compare in Performance',
    portfolio: 'Add to portfolio',
    subscribe: 'Weekly report',
    curveVsSpy: 'REBASED EQUITY vs EQUITY INDEX = 100',
    pros: 'Why it exists',
    cons: 'Trade-offs',
    deepDive: 'Full analysis',
  },
  strategies: {
    kicker: 'SYSTEMATIC STRATEGIES',
    colStrategy: 'STRATEGY',
    volShort: 'VOL',
    modelsCtaTitle: 'Model portfolios',
    modelsCtaBody: 'Ready-made mixes of these strategies, with the same engine as the portfolio builder.',
    openCard: 'Open strategy →',
    intradayCardHint: 'Inside: pick the exposure, 50% to 500% of the account.',
    filterAll: 'All',
    liveBadge: 'LIVE',
    expandGroup: 'Show all {n} ▸',
    collapseGroup: 'Show fewer ▴',
    seeFamily: 'See all {n} →',
    sizing: {
      title: 'Intraday sizing',
      exposure: 'Exposure',
      exposureHint: '{pct} of the account in the Nasdaq while a position is open; the number of contracts follows from the account size.',
      wholeContracts: 'Contracts are whole: at this exposure one contract needs at least {min} of account, and $100,000 would trade {n} today.',
      grossNote: 'Before commissions and slippage.',
    },
    variant: {
      label: 'Version',
      standard: 'Standard',
      ucits: 'UCITS',
      cardHint: 'Inside: Standard or UCITS version.',
      notes: {
        heimdall: 'UCITS is the EU framework for funds sold to European investors: same signal, UCITS-eligible vehicles and concentration limits, shown in USD.',
        metis: 'UCITS is the EU framework for funds sold to European investors: same signal, gold and commodities capped at 20% each, the rest in cash, shown in USD.',
      },
    },
    strategyDescriptions: {
      tyr: 'All-weather rotation',
      ares: 'Market timing',
      ascalafo: 'Two-layer timing',
      deimos: 'Timing + risk',
      fobos: 'Two-layer + risk',
      bes: 'Trend following',
      janus: 'Market-neutral',
      hraesvelgr: 'Aggressive all-weather',
      eos: 'Intraday Nasdaq futures',
      artemis: 'One trade per session',
      helios: 'Hourly trend continuation',
      hyperion: 'Daylight trend, long only',
      horai: 'Diversified intraday programme',
      theia: 'Intraday programme, long and short',
      astraeus: 'One decision a day, either way',
      phaethon: 'Rides the day’s drive',
      hemera: 'After the open, either way',
      selene: 'Patient, long only',
      asteria: 'Once a week, Monday only',
      aether: 'Broad intraday programme',
      heimdall: 'Macro-regime allocation',
      metis: 'Multi-asset momentum rotation',
    },
    strategyFullDescriptions: {
      tyr: 'TYR is a diversified All-Weather rotation. It spreads risk across uncorrelated economic regimes and adjusts exposures with proprietary signals. Named for the Norse god of order and discipline.',
      ares: 'ARES is a systematic market-timing strategy. It reads the structural health of the market as a whole — not a single ticker — and switches between risk-on and risk-off. Validated through 2008, 2020 and 2022. Live on Wikifolio.',
      ascalafo: 'ASCÁLAFO is a two-layer timing system: independent signals that have to agree before exposure turns. Same directional family as ARES, with tighter entries around volatility regimes.',
      deimos: 'DEIMOS is the high-octane timing sleeve. Same health-of-market logic as ARES, more aggressive when trends are clean, harder drawdowns when they are not.',
      fobos: 'FOBOS adds a volatility overlay on top of leveraged timing. It de-risks when volatility regimes turn hostile and re-engages when they calm.',
      bes: 'BES is multi-horizon equity momentum. It keeps names that are outrunning the broad market on several time scales, and cuts those that are not.',
      janus: 'JANUS is long/short market-neutral stock selection. Weekly rebalance, beta near zero: a diversifier, not a market call.',
      hraesvelgr: 'HRÆSVELGR is TYR’s more aggressive twin in the All Weather family. Same regime discipline, more appetite for growth, and a faster shift to defence when momentum fades.',
      eos: 'EOS is named for the Greek goddess of dawn. It trades Nasdaq futures in the first hours of the session: a systematic, confirmed entry in the direction the day is leaning, fixed stop and target, flat before the close. Audited with YGGDRASIL: Sharpe near 1.5, zero beta to the index. Shown at 300% exposure, before commissions.',
      artemis: 'ARTEMIS is named for the Greek archer goddess: one arrow per session. A single systematic position in Nasdaq futures early in the session, long or short, with a fixed stop and target, flat at the close, and it sits out the main macro days. Audited at one fixed contract: Sharpe 1.3, zero beta. Shown at 300% exposure, before commissions.',
      hyperion: 'HYPERION is named for the Titan of light, the one who watches from above. It trades Nasdaq futures intraday and long only: it waits for the day to show its hand, joins it with a fixed stop and target, and is out before the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, zero beta. Shown at 300% exposure, before commissions.',
      helios: 'HELIOS is named for the sun crossing the sky: it follows the path the day is taking. Once an hour it checks whether Nasdaq futures are drifting with conviction in one direction and, if so, joins that move with a fixed stop and target, a few times per session at most, always flat at the close. Audited at one fixed contract: Sharpe 1.1, zero beta. Shown at 300% exposure, before commissions.',
      horai: 'HORAI is named for the Greek goddesses of the hours, who share out the day between them. A diversified intraday programme on Nasdaq futures: the account is spread across independent systematic decisions taken at different points of the session, all flat at the close. Those decisions are only weakly correlated, so it keeps most of the return with a fraction of the drawdown of any single approach. Shown at 300% exposure, before commissions.',
      theia: 'THEIA is named for the Titaness of sight, the one who sees the whole day at once. A systematic intraday programme on Nasdaq futures, long and short, with several decisions across the session and nothing held overnight. Audited with YGGDRASIL. Shown at 300% exposure, before commissions.',
      astraeus: 'ASTRAEUS is named for the Titan of dusk, father of the stars and of the four winds. It trades Nasdaq futures intraday, long or short, with at most one decision a day and nothing held into the night. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, zero beta. Shown at 300% exposure, before commissions.',
      phaethon: 'PHAETHON is named for the son of Helios, who took the chariot of the sun for a day. It trades Nasdaq futures intraday, long or short, riding the day’s momentum with a fixed stop and out before the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.4, zero beta. Shown at 300% exposure, before commissions.',
      hemera: 'HEMERA is named for the goddess of day, daughter of Night, who brings the light each morning. It trades Nasdaq futures intraday, long or short, once the open has shown its hand, with fixed stop and target and flat at the close. Audited with YGGDRASIL at one fixed contract: Sharpe 1.3, zero beta. Shown at 300% exposure, before commissions.',
      selene: 'SELENE is named for the Titaness of the moon, sister of Helios and Eos. It trades Nasdaq futures intraday and long only, a few days a month, the ones she chooses, and is flat at the close. Audited with YGGDRASIL at one fixed contract: not accepted, one hard rule failed. Shown at 300% exposure, before commissions.',
      asteria: 'ASTERIA is named for the Titaness of falling stars, sister of Leto, who became the island of Delos. It trades Nasdaq futures long only, once a week: it buys on Sunday evening for the Monday session and is out before Monday’s close, at a fixed target or on the clock, with no stop. Audited with YGGDRASIL at one fixed contract: accepted, Sharpe 1.3, no hard rule failed; the minimum-sample rule (300 trades) was switched off by the owner because it trades once a week. Shown at 300% exposure, before commissions.',
      aether: 'AETHER is named for the bright upper air the gods breathe, above the weather. A systematic intraday programme on Nasdaq futures, long and short, with several decisions across the session and flat at every close. Shown at 300% exposure, before commissions.',
      heimdall: 'HEIMDALL is named for the watchman of Bifröst, who sees and hears everything and sounds the Gjallarhorn when change comes. Every month it reads US growth and inflation from the data exactly as they were published at the time, turns them into the odds of four regimes and spreads the portfolio across equities, Treasuries, TIPS, gold, commodities and T-bills; a weekly risk overlay scales exposure down when volatility or stress rises. Two versions, same signal: Standard and UCITS. The Standard version was audited with YGGDRASIL: accepted.',
      metis: 'METIS is named for the Titaness of wise counsel, first wife of Zeus and mother of Athena. On the last trading day of each month it holds the three of eight markets (equities, Treasuries, gold, commodities, real estate) that have risen most over six months, only if they are up, and cash otherwise; a volatility brake scales it down above 15%. Two versions, same signal: Standard and UCITS. The Standard version was audited with YGGDRASIL: accepted. The UCITS version was not accepted (two hard rules failed) and is published by the owner\'s decision.',
    },
  },
  home: {
    ticker: {
      subtitle: 'Systematic research · live track record',
    },
    hero: {
      lead: 'Twenty-two systematic strategies. Several have been live for years — you can follow them on Wikifolio, Darwinex and eToro. Open each page for its numbers.',
      live: 'Live track record',
      ctaReports: 'Weekly reports',
      ctaTerminal: 'Open Terminal',
    },
    regimeTeaser: {
      kicker: 'PROPRIETARY',
      title: 'The HMA regime detector',
      body: 'Six hidden-Markov models. One composite, walk-forward. The live desk around it. Subscribe to open.',
      cta: 'See the Terminal',
    },
    featured: {
      subtitle: 'Directional · market health',
      cta: 'Open ARES →',
      description: 'Market timing from a health composite: stay invested when conditions support it, step aside when they do not. Live on Wikifolio.',
    },
    families: {
      title: 'Six families',
      subtitle: 'Same research process, different jobs in a portfolio. Open a family to see the strategies.',
    },
    snapshot: {
      label: 'EQUITY, SAME START',
      title: 'TYR vs equity indices',
      hint: 'Same start date, rebased to 100. Log scale so TYR and the indices stay readable together.',
      cta: 'Open Performance',
    },
    products: {
      title: 'What you can subscribe to',
      reportsKicker: 'RESEARCH',
      reportsTitle: 'Weekly reports',
      reportsBody: 'Methodology, portfolio evolution and the week that just closed — per strategy or the full bundle.',
      reportsCta: 'See plans',
      terminalKicker: 'REGIME',
      terminalTitle: 'HMA Terminal',
      terminalBody: 'HMA’s proprietary six-factor regime detector — plus the live desk around it. Open it.',
      terminalCta: 'Open Terminal',
    },
    instTeaser: {
      title: 'Mandates, not a product page',
      body: 'Strategy design, regime research and risk overlays for funds, family offices and desks that need a builder.',
      cta: 'See the mandate page',
    },
  },
  insights: {
    title: 'Research',
    subtitle: 'Quantitative notes on Substack. The calendar and the regime engine live in the Terminal.',
    marketContext: 'Market context',
    marketContextHint: 'Full regime, STIR and liquidity tools are in the Terminal.',
    openTerminal: 'Open Terminal',
    whereToInvestSubtitle: 'Several strategies run live, with a real track record you can follow on regulated platforms.',
    readOnSubstack: 'Read on Substack →',
    strategyNamed: 'Strategy {name}',
  },
  weeklyReports: {
    revealPrice: 'See price and subscribe →',
    universeLabel: 'Universe',
    popular: 'Popular',
    universe: {
      diversified: 'Diversified ETFs',
      index: 'Index ETFs',
      equities: 'Listed equities',
      leveraged: 'Leveraged ETFs',
      futures: 'Nasdaq futures',
    },
    cards: {
      TYR: 'All-Weather regime rotation — weekly research report',
      HRAESVELGR: 'Aggressive All Weather rotation — weekly research report',
      ARES: 'Macro timing and protection — weekly research report',
      JANUS: 'Long/short market-neutral — weekly research report',
      BES: 'Multi-horizon equity momentum — weekly research report',
      ASCALAFO: 'Macro timing with a volatility overlay — weekly research report',
      DEIMOS: 'Aggressive macro timing — weekly research report',
      FOBOS: 'Leveraged timing with a volatility overlay — weekly research report',
      EOS: 'Intraday Nasdaq futures — weekly research report',
      ARTEMIS: 'One trade per session on Nasdaq futures — weekly research report',
      HELIOS: 'Hourly trend continuation on Nasdaq futures — weekly research report',
      HYPERION: 'Daylight trend on Nasdaq futures, long only — weekly research report',
      HORAI: 'Diversified intraday programme on Nasdaq futures — weekly research report',
      THEIA: 'Intraday programme on Nasdaq futures, long and short — weekly research report',
      ASTRAEUS: 'One decision a day on Nasdaq futures, long and short — weekly research report',
      PHAETHON: 'Momentum on Nasdaq futures, long and short — weekly research report',
      HEMERA: 'After the open on Nasdaq futures, long and short — weekly research report',
      SELENE: 'Patient long-only days on Nasdaq futures — weekly research report',
      ASTERIA: 'Monday long on Nasdaq futures, once a week — weekly research report',
      AETHER: 'Broad intraday programme on Nasdaq futures — weekly research report',
      HEIMDALL: 'Macro-regime multi-asset allocation, Standard and UCITS — weekly research report',
      METIS: 'Multi-asset momentum rotation, Standard and UCITS — weekly research report',
    },
    disclaimerLabel: 'Disclaimer',
    disclaimer: 'These reports are for informational and educational purposes only. They describe the quantitative methodology, mathematical models, and portfolio evolution of each strategy. They do NOT constitute trading signals, investment advice, or personalized financial recommendations. Past performance does not guarantee future results. Always conduct your own research and consult with a licensed financial advisor before making investment decisions.',
    notAvailable: 'This strategy is not yet available for purchase',
    bundleNotAvailable: 'Bundle is not yet available for purchase',
    checkoutFailed: 'Failed to create checkout session',
    checkoutStartFailed: 'Failed to start checkout. Please try again.',
  },
  checkout: {
    missingPrice: 'This product is not yet available for purchase — missing Stripe price configuration.',
    checkoutFailed: 'Could not start checkout',
    checkoutError: 'Checkout error: {message}',
    noSubscription: 'No active subscription found',
    portalFailed: 'Failed to open subscription management. Please try again.',
  },
  contact: {
    fillAll: 'Please fill all fields',
    sent: 'Message sent. I will reply soon.',
    sendError: 'Error sending message. Try again.',
    connectionError: 'Connection error. Try again later.',
    sending: 'Sending…',
    methodLabel: 'Contact method',
    methodHint: 'Use the form to send a message. I typically reply within 24–48 hours.',
  },
  common: {
    present: 'Present',
  },
  performancePage: {
    kicker: 'ANALYTICS',
    title: 'Performance',
    lead: 'Compare strategies with each other and with equity indices. Sharpe, drawdown, correlation and risk metrics, from the published curves.',
    dateRange: 'Date range',
    dateHint: 'Every series is clipped to the overlapping dates in this range and rebased to 100 at the common start.',
    equity: 'Equity curves',
    logScale: 'Log scale',
    showRegime: 'Market regime overlay',
    regimeHint: 'Background colour is the six-factor composite (risk-on → risk-off). It is a market-wide read, not a strategy signal.',
    regimeOn: 'Risk-on',
    regimeNeutral: 'Neutral',
    regimeCautious: 'Cautious',
    regimeOff: 'Risk-off',
    rollingSharpe: 'Rolling Sharpe',
    months: '{n} months',
    drawdown: 'Drawdown',
    underwater: 'underwater',
    correlation: 'Correlation',
    correlationHint: 'Monthly returns · Pearson',
    metrics: 'Comparative metrics',
    strategies: 'Strategies',
    benchmarks: 'Benchmarks',
    selectAll: 'Select all',
    clear: 'Clear',
    needTwo: 'Select at least two series to see correlations',
    needOne: 'Select at least one series to view metrics',
    needOneEquity: 'Select at least one strategy or benchmark',
    needOneDrawdown: 'Select at least one series to view drawdown',
    needOneSharpe: 'Select at least one series to view rolling Sharpe',
    rebased: 'Rebased to 100',
    rebasedFrom: 'Rebased to 100 · {date}',
    series: 'Series',
    totalReturn: 'Total Return',
    volatility: 'Volatility',
    upMonths: '% Up Months',
    metricsNote: 'Metrics are computed over the overlapping window of the selected series',
    metricsNoteRange: 'inside the marked date range',
    metricsNoteAnnual: 'Sharpe and Sortino use daily excess returns over 3-month T-bills, annualized with 252 trading days (weekly curves: weekly returns, 52).',
    maxPreset: 'Max',
    typeStrategy: 'strategy',
    typeBenchmark: 'benchmark',
  },
  portfolioPage: {
    kicker: 'PORTFOLIO BUILDER',
    title: 'Build your portfolio',
    lead: 'Combine HMA strategies with custom weights, choose a rebalancing frequency, and backtest the combined portfolio against the major benchmarks.',
    presets: 'Quick presets',
    loadModel: 'Load a model portfolio',
    findProfile: 'Find my risk profile',
    optimizing: 'Optimizing…',
    optimize: 'Optimize for max Sharpe',
    optimizeFailed: 'Optimization failed — try a different rebalance frequency.',
    components: 'Portfolio components',
    select: 'Select…',
    addComponent: 'Add component',
    totalOk: 'Total: {n}%',
    totalUnder: 'Total: {n}% — {left}% remaining',
    totalOver: 'Total: {n}% — exceeds 100%',
    autoBalance: 'Auto-balance',
    rebalancing: 'Rebalancing frequency',
    rebalanceHint: 'Rebalancing periodically resets weights back to your target allocation.',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annually: 'Annually',
    never: 'Never (buy & hold)',
    logScale: 'Log scale',
    rebased: 'Rebased to 100',
    calculate: 'Calculate portfolio',
    copied: 'Copied!',
    copyLink: 'Copy share link',
    models: 'Model portfolios',
    modelsHint: 'Load a ready-made mix, then calculate. Or build your own on the left.',
    myPortfolio: 'My portfolio',
    allocation: 'Allocation',
    backtestRange: 'Backtest range',
    years: '{n} years',
    rebalanced: 'Rebalanced: {freq}',
    equityCurve: 'Equity curve',
    fullMetrics: 'Full metrics',
    valueBase: 'Value (base 100)',
    returnPct: 'Return (%)',
    sharpeRatio: 'Sharpe ratio',
    maxDrawdown: 'Max drawdown',
    ddDuration: 'DD duration',
    bestYear: 'Best year',
    worstYear: 'Worst year',
    posMonths: 'Pos. months',
    compareVs: 'Compare vs',
    benchmarksHint: 'Dashed lines are reference indexes. They are not part of the portfolio.',
  },
  terminalLanding: {
    badge: 'PROPRIETARY REGIME DETECTOR',
    title: 'A detector for the market’s regime — built in-house.',
    lead: 'Six hidden-Markov models collapse into one composite, from risk-on to risk-off. Around it, the live desk: macro, funding, internals. Open it every day.',
    price: '10,99 € / month.',
    ctaSignIn: 'Sign in to subscribe',
    ctaOpen: 'Open Terminal →',
    ctaBusy: 'Redirecting…',
    ctaUnlock: 'Unlock for €10.99 / month',
    seeInside: 'See the detector',
    subscribed: 'You’re subscribed — access is active.',
    liveNow: 'LIVE COMPOSITE',
    liveAsOf: 'as of {date}',
    regimeEyebrow: 'THE ENGINE',
    regimeTitle: 'The HMA regime detector',
    regimeLead: 'This is the proprietary core of the Terminal. Six independent Gaussian HMMs — volatility, trend, breadth, cycle, liquidity, valuation — each infer a latent state and a posterior-weighted risk. Their equal-weight mean is the composite. Every day is scored walk-forward: only information available up to that day. Not a trade ticket. A read on the tape.',
    regimePoints: [
      { t: 'Proprietary', d: 'Designed and run in-house. The same engine HMA uses to read the market — now on your screen.' },
      { t: 'Six factors', d: 'Volatility, trend, breadth, cycle, liquidity, valuation. Independent models, one composite.' },
      { t: 'Walk-forward', d: 'No look-ahead. Each day is filtered on its own past. A history you can actually trust.' },
      { t: 'Composite & exposure', d: 'One number from 0 (risk-on) to 1 (risk-off). Suggested exposure = 1 − composite, with a 5-day hysteresis.' },
    ],
    shotsEyebrow: 'THE DESK',
    shotsTitle: 'What you open when you subscribe',
    shots: [
      { t: 'Regime detector', d: 'Composite, six-factor board, causal history.' },
      { t: 'Liquidity tape', d: 'Net liquidity, overnight rates, the curve.' },
      { t: 'STIR monitor', d: 'Corridor, reserves, funding stress.' },
      { t: 'Macro dashboard', d: 'Yields, inflation, labour, credit, stress.' },
      { t: 'Market internals', d: 'Participation under the index.' },
      { t: 'Private repo', d: 'Overnight rates, volumes, facility take-up.' },
    ],
    lockedEyebrow: 'LOCKED',
    lockedTitle: 'More panels, behind the lock',
    lockedHint: 'Subscribe to open the factor explorer, credit, facilities and breadth alerts — not just the covers.',
    lockedCta: 'Unlock to open',
    lockedShots: [
      { t: 'Factor explorer' },
      { t: 'Credit & stress' },
      { t: 'Facility take-up' },
      { t: 'Breadth alerts' },
      { t: 'Field guide' },
      { t: 'Overnight funding' },
      { t: 'Factor history' },
      { t: 'Liquidity close-up' },
    ],
    previewKicker: 'LIVE PREVIEW',
    previewTitle: 'The real terminal is running underneath. Subscribe to take the blur off.',
    insideEyebrow: 'WHAT YOU GET',
    insideTitle: 'The detector, then the whole desk',
    features: [
      { t: 'HMA regime detector', d: 'Six independent Gaussian hidden-Markov models. One composite from risk-on to risk-off. Built here, not licensed in.' },
      { t: 'Composite & exposure', d: 'A number from 0 to 1, suggested exposure 1 − composite, and a 5-day hysteresis so the label does not flicker.' },
      { t: 'Walk-forward history', d: 'Every day scored with information available up to that day — no look-ahead. A regime path you can backtest.' },
      { t: 'Factor explorer', d: 'Open any factor: live state, risk path, and the indicators that feed the model.' },
      { t: 'Liquidity tape', d: 'System net liquidity vs the equity index, overnight rates and the 2s10s curve — the tide, in one screen.' },
      { t: 'STIR monitor', d: 'Overnight corridor, tails, reserves vs reverse-repo, funding spreads and money-market assets — where stress shows up first.' },
      { t: 'Market internals', d: '4% movers, 5- and 10-day ratios, 25-day highs/lows and the 13/34 thrust — not just the index.' },
      { t: 'Macro dashboard', d: 'Full yield curve, inflation and labour, HY vs IG credit, financial-conditions indexes, the dollar and oil.' },
      { t: 'Private repo', d: 'Tri-party, DVP and GCF overnight rates and volumes, next to facility take-up and counterparties.' },
      { t: 'Field guide', d: 'Plain-language notes on every series: what it means, why it moves, and how to read it.' },
    ],
    howEyebrow: 'HOW IT WORKS',
    howTitle: 'The detector scores the day. You read it.',
    steps: [
      { n: '01', t: 'Six models score today', d: 'Each factor infers a latent state and a risk in [0,1] — strictly causal, no look-ahead.' },
      { n: '02', t: 'One composite', d: 'Equal-weight mean, hysteresis, suggested exposure. Risk-on through risk-off, in a single number.' },
      { n: '03', t: 'You open the Terminal', d: 'The detector, the history, the explorer and the rest of the desk — ready when you log in.' },
    ],
    membership: 'MEMBERSHIP',
    perMonth: '/ month',
    cancel: 'Cancel anytime · billed monthly',
    bullets: [
      'Proprietary six-factor regime detector',
      'Live composite and suggested exposure',
      'Walk-forward regime history',
      'Factor explorer',
      'Macro, STIR, internals and repo panels',
      'Field guide on every series',
      'Updates every day',
    ],
    faqEyebrow: 'FAQ',
    faqTitle: 'Good to know',
    faq: [
      { q: 'What exactly am I paying for?', a: 'Live access to the Terminal: HMA’s proprietary regime detector, the factor explorer, the causal history, and the rest of the desk — updated daily.' },
      { q: 'What is the regime detector?', a: 'A six-factor hidden-Markov engine built in-house. It does not tell you what to buy. It tells you the market’s risk state, every day, without look-ahead.' },
      { q: 'Is this investment advice?', a: 'No. It is a quantitative research and monitoring tool. Everything is educational and informational — not a recommendation to buy or sell anything.' },
      { q: 'Can I cancel anytime?', a: 'Yes. It is a monthly subscription you can cancel with one click from your account; access runs until the end of the billing period.' },
    ],
    disclaimer: 'The hmaquant Terminal is a quantitative research and monitoring tool provided for educational and informational purposes only. It is not investment advice and nothing in it constitutes a recommendation to buy or sell any security.',
  },
  reportsFaq: {
    title: 'FAQ',
    items: [
      { q: 'What am I buying?', a: 'A weekly research report on that strategy: methodology, portfolio evolution and the previous week. It is not a trade ticket and not personalized advice.' },
      { q: 'Can I cancel?', a: 'Yes. Manage the subscription from your account. Access continues until the end of the billing period.' },
      { q: 'What is the annual plan?', a: 'Ten months billed, two months free. Same report, yearly invoice.' },
      { q: 'Does the bundle include the Terminal?', a: 'The bundle covers the twenty-two strategy reports and macro research notes. The Terminal is a separate €10.99 product.' },
    ],
  },
  footer: {
    tagline: 'Systematic research. Twenty-two strategies, one process.',
    intradayNote: 'Intraday futures curves (EOS, ARTEMIS, HELIOS, HYPERION, HORAI, THEIA, ASTRAEUS, PHAETHON, HEMERA, SELENE, ASTERIA, AETHER) are shown before commissions and slippage, at a constant exposure of 300% of the account (contracts follow the account size), on a backtest replicated from the YGGDRASIL audit.',
  },
  accountHub: {
    reports: 'My reports',
    terminal: 'Terminal',
    lab: 'Quant Lab',
  },
  quantLabPage: {
    kicker: 'SANDBOX',
    blurb: 'Educational simulations — GBM, mean reversion, hidden Markov, greeks. Not the live desk.',
    openTerminal: 'Open Terminal',
    versionBar: 'QUANT LAB v1.0 · HMA QUANT STRATEGIES',
  },
  terminalChrome: {
    lab: 'Educational sandbox · not investment advice',
    modules: '{n} modules',
  },
  institutionalPage: {
    kicker: 'MANDATES',
    heroTitle: 'Built for your book. Not a product catalogue.',
    heroBody: 'HMA takes institutional research mandates — systematic design, regime work and risk overlays — for funds, family offices and desks that need a builder, not another newsletter.',
    ctaPrimary: 'Brief a mandate',
    ctaSecondary: 'How we work',
    proofKicker: 'IN PUBLIC',
    proof: [
      { value: '12', label: 'systematic strategies running live' },
      { value: 'Live', label: 'Track record on Wikifolio, Darwinex and eToro' },
      { value: '6 HMM', label: 'proprietary regime engine, walk-forward' },
      { value: 'Desk', label: 'Physics, quantitative finance, buy-side and broker seats' },
    ],
    forTitle: 'Who should write',
    forBody: 'If you already know the question, we can scope in a call. If you need a vendor brochure, this is the wrong page.',
    for: [
      { title: 'Family office / allocator', body: 'A sleeve that has to earn its keep next to the rest of the book. We design the process, the risk budget and the kill-switch — not a pitch deck of backtests.' },
      { title: 'CIO / risk', body: 'A regime overlay, a drawdown protocol, or an independent read on whether the current book is still the book you thought you had.' },
      { title: 'Systematic desk', body: 'A module you do not want to staff internally: signal research, walk-forward, a monitoring spec, or a second pair of eyes on a live process.' },
    ],
    workTitle: 'Mandates we take',
    workBody: 'The public site is the work sample. A mandate goes further: your data, your constraints, your book.',
    work: [
      { title: 'Systematic strategy design', body: 'From hypothesis to a process you can run. Universe, signals, execution assumptions, walk-forward, and the failure modes written down before capital is at risk.', tag: 'New sleeve · rebuild' },
      { title: 'Regime and macro research', body: 'The six-factor HMM stack that sits in the Terminal, specified to your book: when to add risk, when to cut, what neutral actually means for you.', tag: 'Overlay · timing' },
      { title: 'Risk overlays and drawdown protocols', body: 'Kill-switches, vol targeting, stress paths, and operating rules a CIO can defend in a committee — not a VaR number on a slide.', tag: 'Risk · overlay' },
      { title: 'Portfolio construction', body: 'How the pieces sit together. Risk budgets, correlation regimes, and what to do when two diversifiers start moving as one.', tag: 'Family office · multi-strategy' },
      { title: 'Retained research', body: 'A weekly or monthly cadence: notes, diagnostics, and a named person who already knows the book. Closer to a desk than a consulting PDF.', tag: 'Ongoing' },
    ],
    processTitle: 'How an engagement runs',
    processBody: 'Short scope, written assumptions, you keep the work.',
    process: [
      { n: '01', title: 'Brief', body: 'You send the question, the constraints, and what done looks like. We say yes, no, or not yet.' },
      { n: '02', title: 'Scope', body: 'A short note: deliverables, data we need, timeline, fee. No fishing expedition.' },
      { n: '03', title: 'Build', body: 'Research in the open with you. Code, charts, and assumptions written so someone else could rerun them.' },
      { n: '04', title: 'Hand-off', body: 'You keep the work. Optional retain to keep the process alive as the regime changes.' },
    ],
    deliverTitle: 'What you leave with',
    deliver: [
      { title: 'A written process', body: 'Not a black box. Signals, data, and the rules for when it is allowed to trade.' },
      { title: 'A research pack', body: 'Walk-forward, failure cases, and the charts a committee can actually read.' },
      { title: 'Code if you want it', body: 'Reproducible notebooks or a small service. Yours to run.' },
      { title: 'An operating spec', body: 'What to monitor, what to kill, who gets the alert.' },
    ],
    notTitle: 'What this is not',
    notItems: [
      'We do not warehouse your AUM. The live track record is on regulated platforms — proof of process, not a fund raise.',
      'We do not sell a signal dump or a model with no owner.',
      'We do not take every brief. If it is a newsletter, the reports page is the product. If it is a desk problem, write.',
    ],
    sampleTitle: 'The public work sample',
    sampleBody: 'Strategies, Performance and the Terminal are what we already run. A mandate is the same craft, specified to you.',
    samples: [
      { to: '/strategies', label: 'Twenty-two strategies' },
      { to: '/performance', label: 'Performance' },
      { to: '/terminal', label: 'Regime desk' },
    ],
    formKicker: 'INTAKE',
    formTitle: 'Send a short brief',
    formBody: 'Firm, the problem, the constraint. We reply ourselves.',
    formName: 'Name',
    formFirm: 'Firm',
    formEmail: 'Email',
    formNeed: 'What you need',
    formNeedPlaceholder: 'Select a mandate',
    formMessage: 'The brief',
    formSend: 'Send brief',
    formSending: 'Sending…',
    formOk: 'Received. We will come back to you.',
    formErr: 'Could not send from here — use the mail link and we will still get it.',
    formMail: 'Prefer email →',
    formNote: 'Confidential. No sales queue. If the brief is not a fit, we will say so.',
    needs: [
      { id: 'strategy', label: 'Strategy design' },
      { id: 'regime', label: 'Regime / overlay' },
      { id: 'risk', label: 'Risk / drawdown protocol' },
      { id: 'portfolio', label: 'Portfolio construction' },
      { id: 'retain', label: 'Retained research' },
      { id: 'other', label: 'Something else — I will explain' },
    ],
    ctaLine: 'If the question is already keeping you up, write it down.',
    ctaPrimary: 'Brief a mandate',
  },
};

export const iaEs = {
  nav: {
    portfolio: 'Cartera',
    research: 'Investigación',
    reports: 'Informes',
    quantLab: 'Lab cuantitativo',
    macroTerminal: 'Terminal',
    institutional: 'Institucional',
    more: 'Más',
    account: 'Cuenta',
    login: 'Entrar',
    about: 'Sobre mí',
  },
  taxonomy: taxonomyEs,
  disclaimer: {
    label: 'Aviso',
    body: 'Las curvas de backtest e investigación son educativas. No son asesoramiento ni una recomendación. Rentabilidades pasadas no garantizan resultados futuros. El trading en vivo incluye costes que los gráficos ignoran.',
  },
  strategyDetail: {
    compare: 'Comparar en Rendimiento',
    portfolio: 'Añadir a la cartera',
    subscribe: 'Informe semanal',
    curveVsSpy: 'CAPITAL REBASADO vs ÍNDICE = 100',
    pros: 'Por qué existe',
    cons: 'Contrapartidas',
    deepDive: 'Análisis completo',
  },
  strategies: {
    kicker: 'ESTRATEGIAS SISTEMÁTICAS',
    colStrategy: 'ESTRATEGIA',
    volShort: 'VOL',
    modelsCtaTitle: 'Carteras modelo',
    modelsCtaBody: 'Mezclas listas de estas estrategias, con el mismo motor que el constructor.',
    openCard: 'Abrir ficha →',
    intradayCardHint: 'Dentro eliges la exposición: del 50 % al 500 % de la cuenta.',
    filterAll: 'Todas',
    liveBadge: 'REAL',
    expandGroup: 'Ver las {n} ▸',
    collapseGroup: 'Ver menos ▴',
    seeFamily: 'Ver las {n} →',
    sizing: {
      title: 'Tamaño intradía',
      exposure: 'Exposición',
      exposureHint: '{pct} de la cuenta en el Nasdaq mientras hay posición; el número de contratos se deduce del tamaño de la cuenta.',
      wholeContracts: 'Los contratos son enteros: con esta exposición un contrato necesita al menos {min} de cuenta, y 100.000 $ operarían hoy {n}.',
      grossNote: 'Sin comisiones ni deslizamiento.',
    },
    variant: {
      label: 'Versión',
      standard: 'Estándar',
      ucits: 'UCITS',
      cardHint: 'Dentro: versión Estándar o UCITS.',
      notes: {
        heimdall: 'UCITS es el marco de la UE para los fondos que se venden a inversores europeos: misma señal, vehículos aptos para UCITS y límites de concentración, en USD.',
        metis: 'UCITS es el marco de la UE para los fondos que se venden a inversores europeos: misma señal, oro y materias primas como mucho al 20 % cada uno, el resto en liquidez, en USD.',
      },
    },
    strategyDescriptions: {
      tyr: 'Rotación todo clima',
      ares: 'Timing de mercado',
      ascalafo: 'Doble capa',
      deimos: 'Timing + riesgo',
      fobos: 'Doble capa + riesgo',
      bes: 'Seguimiento de tendencia',
      janus: 'Neutral al mercado',
      hraesvelgr: 'Todo clima agresivo',
      eos: 'Futuros del Nasdaq intradía',
      artemis: 'Una operación por sesión',
      helios: 'Continuación horaria',
      hyperion: 'Tendencia diurna, solo largos',
      horai: 'Programa intradía diversificado',
      theia: 'Programa intradía, largos y cortos',
      astraeus: 'Una decisión al día, en ambos sentidos',
      phaethon: 'Cabalga el impulso del día',
      hemera: 'Tras la apertura, en ambos sentidos',
      selene: 'Paciente, solo largos',
      asteria: 'Una vez por semana, solo el lunes',
      aether: 'Programa intradía amplio',
      heimdall: 'Asignación por régimen macro',
      metis: 'Rotación multiactivo por momentum',
    },
    strategyFullDescriptions: {
      tyr: 'TYR es una rotación todo clima. Reparte el riesgo entre regímenes económicos poco correlacionados y ajusta las exposiciones con señales propias. El nombre es el dios nórdico del orden y la disciplina.',
      ares: 'ARES es timing sistemático. Lee la salud estructural del mercado — no un ticker — y pasa de riesgo activo a riesgo apagado. Validada en 2008, 2020 y 2022. En vivo en Wikifolio.',
      ascalafo: 'ASCÁLAFO es un sistema de timing a dos capas: las señales independientes tienen que coincidir antes de girar la exposición. Misma familia que ARES, entradas más finas en regímenes de volatilidad.',
      deimos: 'DEIMOS es el tramo de timing de alto octanaje. La misma lógica de salud de mercado que ARES, más agresiva cuando la tendencia está limpia, drawdowns más duros cuando no.',
      fobos: 'FOBOS añade una capa de volatilidad sobre el timing apalancado. Recorta riesgo cuando el régimen de volatilidad se pone hostil y vuelve a entrar cuando se calma.',
      bes: 'BES es impulso de renta variable a varios horizontes. Se queda con lo que va por delante del mercado amplio y corta lo que no.',
      janus: 'JANUS es selección largo/corto neutral al mercado. Rebalanceo semanal, beta cerca de cero: un diversificador, no una apuesta direccional.',
      hraesvelgr: 'HRÆSVELGR es el gemelo más agresivo de TYR en la familia todo clima. La misma disciplina de regímenes, más apetito de crecimiento, y un giro más rápido a defensa cuando el impulso se apaga.',
      eos: 'EOS toma su nombre de la diosa griega del amanecer. Opera futuros del Nasdaq en las primeras horas de la sesión: una entrada sistemática y confirmada en la dirección hacia la que se inclina el día, stop y objetivo fijos, y plana antes del cierre. Auditada con YGGDRASIL: Sharpe cerca de 1,5 y beta cero frente al índice. Mostrada con una exposición del 300 %, sin comisiones.',
      artemis: 'ARTEMIS toma su nombre de la diosa arquera griega: una flecha por sesión. Una sola posición sistemática en futuros del Nasdaq al principio de la sesión, larga o corta, con stop y objetivo fijos, plana al cierre, y sin operar los grandes días macro. Auditada a un contrato fijo: Sharpe 1,3 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      hyperion: 'HYPERION toma su nombre del titán de la luz, el que vigila desde lo alto. Opera futuros del Nasdaq intradía y solo largos: espera a que el día enseñe sus cartas, se suma con stop y objetivo fijos y está fuera antes del cierre. Auditada con YGGDRASIL a un contrato fijo: Sharpe 1,4 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      helios: 'HELIOS toma su nombre del sol recorriendo el cielo: sigue el camino que va tomando el día. Una vez por hora comprueba si los futuros del Nasdaq derivan con convicción en una dirección y, si es así, se suma a ese movimiento con stop y objetivo fijos, unas pocas veces por sesión como máximo, siempre plana al cierre. Auditada a un contrato fijo: Sharpe 1,1 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      horai: 'HORAI toma su nombre de las diosas griegas de las horas, que se reparten el día. Es un programa intradía diversificado sobre futuros del Nasdaq: la cuenta se reparte entre decisiones sistemáticas independientes tomadas en distintos momentos de la sesión, todas planas al cierre. Esas decisiones están poco correlacionadas, así que conserva casi todo el retorno con una fracción de la caída de cualquier enfoque aislado. Mostrada con una exposición del 300 %, sin comisiones.',
      theia: 'THEIA toma su nombre de la titánide de la vista, la que ve el día entero de una vez. Es un programa intradía sistemático sobre futuros del Nasdaq, largo y corto, con varias decisiones a lo largo de la sesión y nada abierto de noche. Auditada con YGGDRASIL. Mostrada con una exposición del 300 %, sin comisiones.',
      astraeus: 'ASTRAEUS toma su nombre del titán del crepúsculo, padre de las estrellas y de los cuatro vientos. Opera futuros del Nasdaq intradía, largo o corto, con una decisión al día como mucho y nada abierto de noche. Auditada con YGGDRASIL a un contrato fijo: Sharpe 1,4 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      phaethon: 'PHAETHON toma su nombre del hijo de Helios, el que cogió el carro del sol por un día. Opera futuros del Nasdaq intradía, largo o corto, cabalgando el impulso del día con un stop fijo y fuera antes del cierre. Auditada con YGGDRASIL a un contrato fijo: Sharpe 1,4 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      hemera: 'HEMERA toma su nombre de la diosa del día, hija de la Noche, la que trae la luz cada mañana. Opera futuros del Nasdaq intradía, largo o corto, cuando la apertura ya ha enseñado sus cartas, con stop y objetivo fijos y plana al cierre. Auditada con YGGDRASIL a un contrato fijo: Sharpe 1,3 y beta cero. Mostrada con una exposición del 300 %, sin comisiones.',
      selene: 'SELENE toma su nombre de la titánide de la luna, hermana de Helios y de Eos. Opera futuros del Nasdaq intradía y solo largos, unos pocos días al mes, los que ella elige, y está plana al cierre. Auditada con YGGDRASIL a un contrato fijo: no aceptada, falló una regla dura. Mostrada con una exposición del 300 %, sin comisiones.',
      asteria: 'ASTERIA toma su nombre de la titánide de las estrellas fugaces, hermana de Leto, la que se convirtió en la isla de Delos. Opera futuros del Nasdaq solo largos, una vez por semana: compra el domingo por la tarde para la sesión del lunes y sale antes del cierre del lunes, en un objetivo fijo o por la hora, sin stop. Auditada con YGGDRASIL a un contrato fijo: aceptada, Sharpe 1,3, ninguna regla dura fallada; la regla de muestra mínima (300 operaciones) se desactivó por decisión del propietario, porque opera una vez por semana. Mostrada con una exposición del 300 %, sin comisiones.',
      aether: 'AETHER toma su nombre del aire luminoso de lo alto, el que respiran los dioses, por encima del tiempo que haga. Es un programa intradía sistemático sobre futuros del Nasdaq, largo y corto, con varias decisiones a lo largo de la sesión y plano en cada cierre. Mostrada con una exposición del 300 %, sin comisiones.',
      heimdall: 'HEIMDALL toma su nombre del vigía del Bifröst, el que todo lo ve y todo lo oye y hace sonar el Gjallarhorn cuando llega el cambio. Cada mes lee el crecimiento y la inflación de EE. UU. con los datos tal y como se publicaron en su momento, los convierte en probabilidades de cuatro regímenes y reparte la cartera entre bolsa, Treasuries, TIPS, oro, materias primas y letras del Tesoro; un overlay semanal de riesgo reduce la exposición cuando sube la volatilidad o aparece tensión. Dos versiones con la misma señal: Estándar y UCITS. La versión Estándar se auditó con YGGDRASIL: aceptada.',
      metis: 'METIS toma su nombre de la titánide del buen consejo, primera esposa de Zeus y madre de Atenea. El último día hábil de cada mes se queda con los tres de ocho mercados (bolsa, Treasuries, oro, materias primas, inmobiliario) que más han subido en seis meses, solo si suben, y si no, liquidez; un freno de volatilidad la reduce por encima del 15 %. Dos versiones con la misma señal: Estándar y UCITS. La versión Estándar se auditó con YGGDRASIL: aceptada. La versión UCITS no fue aceptada (falló dos reglas duras) y se publica por decisión del propietario.',
    },
  },
  home: {
    ticker: {
      subtitle: 'Investigación sistemática · track record en real',
    },
    hero: {
      lead: 'Veintidós estrategias sistemáticas. Varias llevan años en real y se pueden seguir en Wikifolio, Darwinex y eToro. En cada ficha, sus números.',
      live: 'Track record en real',
      ctaReports: 'Informes semanales',
      ctaTerminal: 'Abrir Terminal',
    },
    regimeTeaser: {
      kicker: 'PROPIO',
      title: 'El detector de régimen HMA',
      body: 'Seis modelos ocultos de Markov. Un compuesto, walk-forward. El desk en vivo alrededor. Suscríbete para abrirlo.',
      cta: 'Ver el Terminal',
    },
    featured: {
      subtitle: 'Direccional · salud de mercado',
      cta: 'Abrir ARES →',
      description: 'Timing con un compuesto de salud de mercado: invertido cuando las condiciones lo sostienen, fuera cuando no. En vivo en Wikifolio.',
    },
    families: {
      title: 'Seis familias',
      subtitle: 'El mismo proceso de investigación, distintos trabajos en una cartera. Entra en una familia para ver las estrategias.',
    },
    snapshot: {
      label: 'CAPITAL, MISMO INICIO',
      title: 'TYR frente a índices',
      hint: 'Misma fecha de inicio, rebasado a 100. Escala log para leer TYR y los índices a la vez.',
      cta: 'Abrir Rendimiento',
    },
    products: {
      title: 'A qué te puedes suscribir',
      reportsKicker: 'INVESTIGACIÓN',
      reportsTitle: 'Informes semanales',
      reportsBody: 'Metodología, evolución de la cartera y la semana que acaba de cerrar — por estrategia o el paquete.',
      reportsCta: 'Ver planes',
      terminalKicker: 'RÉGIMEN',
      terminalTitle: 'HMA Terminal',
      terminalBody: 'El detector de régimen propietario de HMA — y el desk en vivo alrededor. Ábrelo.',
      terminalCta: 'Abrir Terminal',
    },
    instTeaser: {
      title: 'Mandates, no una ficha de producto',
      body: 'Diseño de estrategias, régimen y overlays de riesgo para fondos, family offices y mesas que necesitan a alguien que construya.',
      cta: 'Ver la página de mandates',
    },
  },
  insights: {
    title: 'Investigación',
    subtitle: 'Notas cuantitativas en Substack. El calendario y el motor de régimen están en el Terminal.',
    marketContext: 'Contexto de mercado',
    marketContextHint: 'Régimen, STIR y liquidez completa están en el Terminal.',
    openTerminal: 'Abrir Terminal',
    whereToInvestSubtitle: 'Varias estrategias están en real, con un track record que se puede seguir en plataformas reguladas.',
    readOnSubstack: 'Leer en Substack →',
    strategyNamed: 'Estrategia {name}',
  },
  weeklyReports: {
    revealPrice: 'Ver precio y suscribirse →',
    universeLabel: 'Universo',
    popular: 'Destacado',
    universe: {
      diversified: 'ETFs diversificados',
      index: 'ETFs de índices',
      equities: 'Renta variable cotizada',
      leveraged: 'ETFs con apalancamiento',
      futures: 'Futuros del Nasdaq',
    },
    cards: {
      TYR: 'Rotación todo clima — informe semanal de investigación',
      HRAESVELGR: 'Rotación todo clima más agresiva — informe semanal de investigación',
      ARES: 'Timing macro y protección — informe semanal de investigación',
      JANUS: 'Largo/corto neutral al mercado — informe semanal de investigación',
      BES: 'Impulso de renta variable a varios horizontes — informe semanal de investigación',
      ASCALAFO: 'Timing macro con capa de volatilidad — informe semanal de investigación',
      DEIMOS: 'Timing macro agresivo — informe semanal de investigación',
      FOBOS: 'Timing apalancado con capa de volatilidad — informe semanal de investigación',
      EOS: 'Futuros del Nasdaq intradía — informe semanal de investigación',
      ARTEMIS: 'Una operación por sesión en futuros del Nasdaq — informe semanal de investigación',
      HELIOS: 'Continuación de tendencia horaria en futuros del Nasdaq — informe semanal de investigación',
      HYPERION: 'Tendencia diurna en futuros del Nasdaq, solo largos — informe semanal de investigación',
      HORAI: 'Programa intradía diversificado en futuros del Nasdaq — informe semanal de investigación',
      THEIA: 'Programa intradía en futuros del Nasdaq, largos y cortos — informe semanal de investigación',
      ASTRAEUS: 'Una decisión al día en futuros del Nasdaq, largos y cortos — informe semanal de investigación',
      PHAETHON: 'Impulso intradía en futuros del Nasdaq, largos y cortos — informe semanal de investigación',
      HEMERA: 'Tras la apertura en futuros del Nasdaq, largos y cortos — informe semanal de investigación',
      SELENE: 'Días pacientes, solo largos, en futuros del Nasdaq — informe semanal de investigación',
      ASTERIA: 'Largo de lunes en futuros del Nasdaq, una vez por semana — informe semanal de investigación',
      AETHER: 'Programa intradía amplio en futuros del Nasdaq — informe semanal de investigación',
      HEIMDALL: 'Asignación multiactivo por régimen macro, Estándar y UCITS — informe semanal de investigación',
      METIS: 'Rotación multiactivo por momentum, Estándar y UCITS — informe semanal de investigación',
    },
    disclaimerLabel: 'Aviso',
    disclaimer: 'Estos informes son solo informativos y educativos. Describen la metodología cuantitativa, los modelos y la evolución de la cartera de cada estrategia. NO son señales de trading, asesoramiento de inversión ni recomendaciones personalizadas. El rendimiento pasado no garantiza resultados futuros. Haz tu propia investigación y consulta a un asesor autorizado antes de invertir.',
    notAvailable: 'Esta estrategia aún no está disponible para compra',
    bundleNotAvailable: 'El paquete aún no está disponible para compra',
    checkoutFailed: 'No se pudo crear la sesión de pago',
    checkoutStartFailed: 'No se pudo iniciar el pago. Inténtalo de nuevo.',
  },
  checkout: {
    missingPrice: 'Este producto aún no está disponible — falta la configuración de precio de Stripe.',
    checkoutFailed: 'No se pudo iniciar el pago',
    checkoutError: 'Error de pago: {message}',
    noSubscription: 'No hay una suscripción activa',
    portalFailed: 'No se pudo abrir la gestión de suscripciones. Inténtalo de nuevo.',
  },
  contact: {
    fillAll: 'Completa todos los campos',
    sent: 'Mensaje enviado. Responderé pronto.',
    sendError: 'Error al enviar el mensaje. Inténtalo de nuevo.',
    connectionError: 'Error de conexión. Inténtalo más tarde.',
    sending: 'Enviando…',
    methodLabel: 'Método de contacto',
    methodHint: 'Usa el formulario para enviarme un mensaje. Suelo responder en 24–48 horas.',
  },
  common: {
    present: 'Presente',
  },
  performancePage: {
    kicker: 'ANALÍTICA',
    title: 'Rendimiento',
    lead: 'Compara estrategias entre sí y frente a índices. Sharpe, drawdown, correlación y métricas de riesgo, a partir de las curvas publicadas.',
    dateRange: 'Rango de fechas',
    dateHint: 'Cada serie se recorta a las fechas comunes de este rango y se rebasa a 100 en el inicio común.',
    equity: 'Curvas de capital',
    logScale: 'Escala log',
    showRegime: 'Capa de régimen de mercado',
    regimeHint: 'El color de fondo es el compuesto de seis factores (risk-on → risk-off). Es una lectura de mercado, no una señal de estrategia.',
    regimeOn: 'Risk-on',
    regimeNeutral: 'Neutral',
    regimeCautious: 'Cauteloso',
    regimeOff: 'Risk-off',
    rollingSharpe: 'Sharpe rodante',
    months: '{n} meses',
    drawdown: 'Caída máxima',
    underwater: 'bajo el agua',
    correlation: 'Correlación',
    correlationHint: 'Rentabilidades mensuales · Pearson',
    metrics: 'Métricas comparativas',
    strategies: 'Estrategias',
    benchmarks: 'Índices',
    selectAll: 'Seleccionar todas',
    clear: 'Limpiar',
    needTwo: 'Elige al menos dos series para ver correlaciones',
    needOne: 'Elige al menos una serie para ver métricas',
    needOneEquity: 'Elige al menos una estrategia o índice',
    needOneDrawdown: 'Elige al menos una serie para ver el drawdown',
    needOneSharpe: 'Elige al menos una serie para ver el Sharpe rodante',
    rebased: 'Rebasado a 100',
    rebasedFrom: 'Rebasado a 100 · {date}',
    series: 'Serie',
    totalReturn: 'Retorno total',
    volatility: 'Volatilidad',
    upMonths: '% meses al alza',
    metricsNote: 'Las métricas se calculan sobre la ventana común de las series seleccionadas',
    metricsNoteRange: 'dentro del rango de fechas marcado',
    metricsNoteAnnual: 'Sharpe y Sortino usan rendimientos diarios en exceso sobre las letras del Tesoro a 3 meses, anualizados con 252 sesiones (curvas semanales: rendimientos semanales, 52).',
    maxPreset: 'Máx',
    typeStrategy: 'estrategia',
    typeBenchmark: 'índice',
  },
  portfolioPage: {
    kicker: 'CONSTRUCTOR DE CARTERA',
    title: 'Construye tu cartera',
    lead: 'Combina estrategias HMA con pesos a medida, elige la frecuencia de rebalanceo y backtestea el conjunto frente a los índices.',
    presets: 'Presets rápidos',
    loadModel: 'Cargar una cartera modelo',
    findProfile: 'Encontrar mi perfil de riesgo',
    optimizing: 'Optimizando…',
    optimize: 'Optimizar al máximo Sharpe',
    optimizeFailed: 'La optimización falló — prueba otra frecuencia de rebalanceo.',
    components: 'Componentes de la cartera',
    select: 'Elegir…',
    addComponent: 'Añadir componente',
    totalOk: 'Total: {n}%',
    totalUnder: 'Total: {n}% — queda {left}%',
    totalOver: 'Total: {n}% — supera el 100%',
    autoBalance: 'Auto-balancear',
    rebalancing: 'Frecuencia de rebalanceo',
    rebalanceHint: 'El rebalanceo devuelve periódicamente los pesos a tu asignación objetivo.',
    monthly: 'Mensual',
    quarterly: 'Trimestral',
    annually: 'Anual',
    never: 'Nunca (buy & hold)',
    logScale: 'Escala log',
    rebased: 'Rebasado a 100',
    calculate: 'Calcular cartera',
    copied: 'Copiado',
    copyLink: 'Copiar enlace',
    models: 'Carteras modelo',
    modelsHint: 'Carga una mezcla lista y calcula. O arma la tuya a la izquierda.',
    myPortfolio: 'Mi cartera',
    allocation: 'Asignación',
    backtestRange: 'Rango del backtest',
    years: '{n} años',
    rebalanced: 'Rebalanceo: {freq}',
    equityCurve: 'Curva de capital',
    fullMetrics: 'Métricas completas',
    valueBase: 'Valor (base 100)',
    returnPct: 'Retorno (%)',
    sharpeRatio: 'Ratio Sharpe',
    maxDrawdown: 'Caída máxima',
    ddDuration: 'Duración DD',
    bestYear: 'Mejor año',
    worstYear: 'Peor año',
    posMonths: 'Meses al alza',
    compareVs: 'Comparar con',
    benchmarksHint: 'Las líneas discontinuas son índices de referencia. No forman parte de la cartera.',
  },
  terminalLanding: {
    badge: 'DETECTOR DE RÉGIMEN PROPIO',
    title: 'Un detector del régimen de mercado — construido aquí.',
    lead: 'Seis modelos ocultos de Markov se condensan en un compuesto, de risk-on a risk-off. Alrededor, el desk en vivo: macro, funding, internos. Ábrelo cada día.',
    price: '10,99 € / mes.',
    ctaSignIn: 'Entra para suscribirte',
    ctaOpen: 'Abrir Terminal →',
    ctaBusy: 'Redirigiendo…',
    ctaUnlock: 'Activar por 10,99 € / mes',
    seeInside: 'Ver el detector',
    subscribed: 'Estás suscrito — el acceso está activo.',
    liveNow: 'COMPUESTO EN VIVO',
    liveAsOf: 'a {date}',
    regimeEyebrow: 'EL MOTOR',
    regimeTitle: 'El detector de régimen HMA',
    regimeLead: 'Este es el núcleo propio del Terminal. Seis HMM gaussianos independientes — volatilidad, tendencia, amplitud, ciclo, liquidez, valoración — cada uno infiere un estado latente y un riesgo ponderado. Su media es el compuesto. Cada día se puntúa walk-forward: solo con información de ese día. No es una orden. Es una lectura de la cinta.',
    regimePoints: [
      { t: 'Propio', d: 'Diseñado y operado aquí. El mismo motor con el que HMA lee el mercado — ahora en tu pantalla.' },
      { t: 'Seis factores', d: 'Volatilidad, tendencia, amplitud, ciclo, liquidez, valoración. Modelos independientes, un compuesto.' },
      { t: 'Walk-forward', d: 'Sin look-ahead. Cada día se filtra con su propio pasado. Un historial en el que se puede confiar.' },
      { t: 'Compuesto y exposición', d: 'Un número de 0 (risk-on) a 1 (risk-off). Exposición sugerida = 1 − compuesto, con histéresis de 5 días.' },
    ],
    shotsEyebrow: 'EL DESK',
    shotsTitle: 'Lo que abres al suscribirte',
    shots: [
      { t: 'Detector de régimen', d: 'Compuesto, tablero de seis factores, historial causal.' },
      { t: 'Cinta de liquidez', d: 'Liquidez neta, tipos overnight, la curva.' },
      { t: 'Monitor STIR', d: 'Corredor, reservas, estrés de funding.' },
      { t: 'Dashboard macro', d: 'Tipos, inflación, empleo, crédito, estrés.' },
      { t: 'Internos de mercado', d: 'Participación por debajo del índice.' },
      { t: 'Repo privado', d: 'Tipos overnight, volúmenes, uso de facilidades.' },
    ],
    lockedEyebrow: 'BLOQUEADO',
    lockedTitle: 'Más paneles, detrás del candado',
    lockedHint: 'Suscríbete para abrir el explorador de factores, crédito, facilidades y alertas de amplitud — no solo las portadas.',
    lockedCta: 'Desbloquear',
    lockedShots: [
      { t: 'Explorador de factores' },
      { t: 'Crédito y estrés' },
      { t: 'Uso de facilidades' },
      { t: 'Alertas de amplitud' },
      { t: 'Guía de campo' },
      { t: 'Funding overnight' },
      { t: 'Historial de factores' },
      { t: 'Liquidez de cerca' },
    ],
    previewKicker: 'PREVIEW EN VIVO',
    previewTitle: 'El terminal real está corriendo debajo. Suscríbete para quitar el desenfoque.',
    insideEyebrow: 'QUÉ HAY DENTRO',
    insideTitle: 'El detector, y después todo el desk',
    features: [
      { t: 'Detector de régimen HMA', d: 'Seis HMM gaussianos independientes. Un compuesto de risk-on a risk-off. Hecho aquí, no licenciado.' },
      { t: 'Compuesto y exposición', d: 'Un número de 0 a 1, exposición sugerida 1 − compuesto, e histéresis de 5 días para que la etiqueta no parpadee.' },
      { t: 'Historial walk-forward', d: 'Cada día puntuado con información de ese día — sin look-ahead. Una senda de régimen que se puede backtestear.' },
      { t: 'Explorador de factores', d: 'Abre cualquier factor: estado en vivo, trayectoria de riesgo e indicadores que alimentan el modelo.' },
      { t: 'Cinta de liquidez', d: 'Liquidez neta del sistema vs el índice, tipos overnight y la curva 2s10s — la marea, en una pantalla.' },
      { t: 'Monitor STIR', d: 'Corredor overnight, colas, reservas vs reverse-repo, spreads de funding y activos de money-market — donde el estrés aparece primero.' },
      { t: 'Internos de mercado', d: 'Movers 4 %, ratios a 5 y 10 días, máximos/mínimos a 25 días y el empuje 13/34 — no solo el índice.' },
      { t: 'Dashboard macro', d: 'Curva completa, inflación y empleo, crédito HY vs IG, índices de condiciones financieras, dólar y petróleo.' },
      { t: 'Repo privado', d: 'Tipos y volúmenes overnight tri-party, DVP y GCF, junto al uso de facilidades y contrapartes.' },
      { t: 'Guía de campo', d: 'Notas en claro de cada serie: qué significa, por qué se mueve y cómo leerla.' },
    ],
    howEyebrow: 'CÓMO FUNCIONA',
    howTitle: 'El detector puntúa el día. Tú lo lees.',
    steps: [
      { n: '01', t: 'Seis modelos puntúan hoy', d: 'Cada factor infiere un estado latente y un riesgo en [0,1] — estrictamente causal, sin look-ahead.' },
      { n: '02', t: 'Un compuesto', d: 'Media equiponderada, histéresis, exposición sugerida. De risk-on a risk-off, en un solo número.' },
      { n: '03', t: 'Abres el Terminal', d: 'El detector, el historial, el explorador y el resto del desk — listos al entrar.' },
    ],
    membership: 'SUSCRIPCIÓN',
    perMonth: '/ mes',
    cancel: 'Cancela cuando quieras · cobro mensual',
    bullets: [
      'Detector de régimen propietario de seis factores',
      'Compuesto en vivo y exposición sugerida',
      'Historial de régimen walk-forward',
      'Explorador de factores',
      'Paneles de macro, STIR, internos y repo',
      'Guía de campo en cada serie',
      'Actualización cada día',
    ],
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Para enterarte',
    faq: [
      { q: '¿Qué estoy pagando?', a: 'Acceso en vivo al Terminal: el detector de régimen propio de HMA, el explorador de factores, el historial causal y el resto del desk — actualizado cada día.' },
      { q: '¿Qué es el detector de régimen?', a: 'Un motor de seis HMM construido aquí. No te dice qué comprar. Te dice el estado de riesgo del mercado, cada día, sin look-ahead.' },
      { q: '¿Es asesoramiento?', a: 'No. Es una herramienta de research y seguimiento. Todo es educativo e informativo — no una recomendación de compra o venta.' },
      { q: '¿Puedo cancelar cuando quiera?', a: 'Sí. Es una suscripción mensual que cancelas desde la cuenta; el acceso sigue hasta el final del periodo cobrado.' },
    ],
    disclaimer: 'El Terminal de hmaquant es una herramienta de research y seguimiento, educativa e informativa. No es asesoramiento ni una recomendación de compra o venta de ningún valor.',
  },
  reportsFaq: {
    title: 'Preguntas frecuentes',
    items: [
      { q: '¿Qué estoy comprando?', a: 'Un informe semanal de investigación sobre esa estrategia: metodología, evolución de la cartera y la semana previa. No es un ticket de trading ni asesoramiento personalizado.' },
      { q: '¿Puedo cancelar?', a: 'Sí. Gestionas la suscripción desde la cuenta. El acceso sigue hasta el final del periodo facturado.' },
      { q: '¿Qué es el plan anual?', a: 'Diez meses cobrados, dos meses gratis. El mismo informe, factura anual.' },
      { q: '¿El paquete incluye el Terminal?', a: 'El paquete cubre los veintidós informes de estrategia y las notas macro. El Terminal es un producto aparte a €10,99.' },
    ],
  },
  footer: {
    tagline: 'Investigación sistemática. Veintidós estrategias, un proceso.',
    intradayNote: 'Las curvas de las estrategias intradía de futuros (EOS, ARTEMIS, HELIOS, HYPERION, HORAI, THEIA, ASTRAEUS, PHAETHON, HEMERA, SELENE, ASTERIA, AETHER) se muestran sin comisiones ni deslizamiento, con una exposición constante del 300 % de la cuenta (los contratos siguen al tamaño de la cuenta), sobre un backtest replicado de la auditoría de YGGDRASIL.',
  },
  accountHub: {
    reports: 'Mis informes',
    terminal: 'Terminal',
    lab: 'Lab cuantitativo',
  },
  quantLabPage: {
    kicker: 'SANDBOX',
    blurb: 'Simulaciones educativas — GBM, reversión a la media, Markov oculto, greeks. No es el desk en vivo.',
    openTerminal: 'Abrir Terminal',
    versionBar: 'QUANT LAB v1.0 · HMA QUANT STRATEGIES',
  },
  terminalChrome: {
    lab: 'Sandbox educativo · no es asesoramiento',
    modules: '{n} módulos',
  },
  institutionalPage: {
    kicker: 'MANDATES',
    heroTitle: 'Hecho para tu book. No un catálogo de productos.',
    heroBody: 'HMA toma mandates de investigación institucional — diseño sistemático, régimen y overlays de riesgo — para fondos, family offices y mesas que necesitan a alguien que construya, no otro newsletter.',
    ctaPrimary: 'Enviar un brief',
    ctaSecondary: 'Cómo trabajamos',
    proofKicker: 'EN PÚBLICO',
    proof: [
      { value: '12', label: 'estrategias sistemáticas en real' },
      { value: 'Live', label: 'Track record en Wikifolio, Darwinex y eToro' },
      { value: '6 HMM', label: 'motor de régimen propietario, walk-forward' },
      { value: 'Desk', label: 'Física, finanzas cuantitativas, buy-side y bróker' },
    ],
    forTitle: 'Quién debería escribir',
    forBody: 'Si ya tienes la pregunta, se puede acotar en una llamada. Si buscas un brochure de proveedor, esta no es la página.',
    for: [
      { title: 'Family office / allocator', body: 'Un sleeve que tiene que ganarse el sitio junto al resto del book. Diseñamos el proceso, el presupuesto de riesgo y el kill-switch — no un deck de backtests.' },
      { title: 'CIO / riesgo', body: 'Un overlay de régimen, un protocolo de drawdown, o una lectura independiente de si el book actual sigue siendo el que creías tener.' },
      { title: 'Mesa sistemática', body: 'Un módulo que no quieres staffear por dentro: research de señal, walk-forward, spec de monitorización, o un segundo par de ojos sobre un proceso en vivo.' },
    ],
    workTitle: 'Mandates que tomamos',
    workBody: 'El site público es la muestra de trabajo. Un mandate va más lejos: tus datos, tus restricciones, tu book.',
    work: [
      { title: 'Diseño de estrategias sistemáticas', body: 'De la hipótesis a un proceso que se pueda correr. Universo, señales, supuestos de ejecución, walk-forward y los modos de fallo escritos antes de que el capital esté en riesgo.', tag: 'Sleeve nuevo · rebuild' },
      { title: 'Régimen e investigación macro', body: 'El stack de seis HMM que está en el Terminal, especificado a tu book: cuándo añadir riesgo, cuándo cortar, qué significa neutro para ti.', tag: 'Overlay · timing' },
      { title: 'Overlays de riesgo y protocolos de drawdown', body: 'Kill-switches, vol targeting, sendas de estrés y reglas que un CIO puede defender en un comité — no un VaR en una slide.', tag: 'Riesgo · overlay' },
      { title: 'Construcción de cartera', body: 'Cómo encajan las piezas. Presupuestos de riesgo, regímenes de correlación, y qué hacer cuando dos diversificadores empiezan a moverse como uno.', tag: 'Family office · multi-strategy' },
      { title: 'Research en retainer', body: 'Cadencia semanal o mensual: notas, diagnósticos y alguien que ya conoce el book. Más cerca de un desk que de un PDF de consultoría.', tag: 'Continuo' },
    ],
    processTitle: 'Cómo corre un encargo',
    processBody: 'Alcance corto, supuestos por escrito, el trabajo se queda contigo.',
    process: [
      { n: '01', title: 'Brief', body: 'Envías la pregunta, las restricciones y qué significa hecho. Decimos sí, no, o todavía no.' },
      { n: '02', title: 'Alcance', body: 'Una nota corta: entregables, datos que necesitamos, plazos, fee. Sin expedición de pesca.' },
      { n: '03', title: 'Build', body: 'Research a la vista contigo. Código, gráficos y supuestos escritos para que otra persona pueda repetirlos.' },
      { n: '04', title: 'Entrega', body: 'El trabajo se queda contigo. Retainer opcional para mantener el proceso vivo cuando cambia el régimen.' },
    ],
    deliverTitle: 'Con qué te quedas',
    deliver: [
      { title: 'Un proceso escrito', body: 'No una caja negra. Señales, datos y las reglas de cuándo se le deja operar.' },
      { title: 'Un pack de research', body: 'Walk-forward, casos de fallo y gráficos que un comité puede leer de verdad.' },
      { title: 'Código si lo quieres', body: 'Notebooks reproducibles o un servicio pequeño. Para que lo corras tú.' },
      { title: 'Una spec operativa', body: 'Qué monitorizar, qué matar, quién recibe la alerta.' },
    ],
    notTitle: 'Qué no es esto',
    notItems: [
      'No custodamos tu AUM. El track record en vivo está en plataformas reguladas — prueba de proceso, no una captación de fondo.',
      'No vendemos un dump de señales ni un modelo sin dueño.',
      'No tomamos todos los briefs. Si es un newsletter, el producto está en Informes. Si es un problema de desk, escribe.',
    ],
    sampleTitle: 'La muestra pública',
    sampleBody: 'Estrategias, Rendimiento y el Terminal es lo que ya corremos. Un mandate es el mismo oficio, especificado a ti.',
    samples: [
      { to: '/strategies', label: 'Veintidós estrategias' },
      { to: '/performance', label: 'Rendimiento' },
      { to: '/terminal', label: 'Desk de régimen' },
    ],
    formKicker: 'INTAKE',
    formTitle: 'Envía un brief corto',
    formBody: 'Firma, el problema, la restricción. Respondemos nosotros.',
    formName: 'Nombre',
    formFirm: 'Firma',
    formEmail: 'Email',
    formNeed: 'Qué necesitas',
    formNeedPlaceholder: 'Elige un mandate',
    formMessage: 'El brief',
    formSend: 'Enviar brief',
    formSending: 'Enviando…',
    formOk: 'Recibido. Te escribimos.',
    formErr: 'No se pudo enviar desde aquí — usa el enlace de correo y nos llega igual.',
    formMail: 'Escribe por email →',
    formNote: 'Confidencial. Sin cola de ventas. Si el brief no encaja, lo diremos.',
    needs: [
      { id: 'strategy', label: 'Diseño de estrategia' },
      { id: 'regime', label: 'Régimen / overlay' },
      { id: 'risk', label: 'Riesgo / protocolo de drawdown' },
      { id: 'portfolio', label: 'Construcción de cartera' },
      { id: 'retain', label: 'Research en retainer' },
      { id: 'other', label: 'Otra cosa — lo explico' },
    ],
    ctaLine: 'Si la pregunta ya no te deja dormir, escríbela.',
  },
};
